import { ReactComponent as AddIcon } from "assets/icon/add-icon.svg";
import { Box, ExcelGenerator, Typography } from "components";
import { compose, withAuthorize, withHooks, withTranslation } from "enhancers";
import PageContent, { BreadcrumbsProps } from "layouts/PageContent";
import { useMemo } from "react";
import { formatDate, gql, homePath } from "utils/helper";
import CreateHospitalImportModal from "./CreateHospitalImportModal";
import HospitalImportFilters from "./HospitalImportFilters";
import HospitalImportTable from "./HospitalImportTable";
import { PERMISSIONS } from "constants/enums/permissions";

const HospitalImportPage = ({
  t,
  breadcrumbs,
  pageActions,
  isCreateModalOpen,
  handleCloseCreateModal,
}: any) => (
  <PageContent
    title={t(".title")}
    breadcrumbs={breadcrumbs}
    pageActions={pageActions}
    filter={<HospitalImportFilters />}
  >
    <Box>
      <Box display="flex" justifyContent="space-between" mb={4}>
        <Typography variant="Header/20">{t(".title")}</Typography>
      </Box>
      <HospitalImportTable />
    </Box>

    <CreateHospitalImportModal
      isOpen={isCreateModalOpen}
      onClose={handleCloseCreateModal}
    />
  </PageContent>
);

export const HOSPITAL_IMPORT_TABLE_API = {
  CLAIM_REQUEST_EXCEL_TEMPLATES: gql`
    mutation CLAIM_REQUEST_EXCEL_TEMPLATES {
      claimRequestExcelTemplates
    }
  `,
};

const enhancer = compose(
  withAuthorize(),
  withTranslation({
    prefix: "pages.main.hospitalImport.index",
  }),
  withHooks((props: any, hooks: any) => {
    const { t, hasPermission } = props;
    const { useCallback, useState, useMutation } = hooks;

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [claimRequestExcelTemplates] = useMutation(
      HOSPITAL_IMPORT_TABLE_API.CLAIM_REQUEST_EXCEL_TEMPLATES
    );

    const breadcrumbs = useMemo(
      (): BreadcrumbsProps[] => [
        { label: t(".home"), path: homePath() },
        { label: t(".title"), path: null },
      ],
      [t]
    );

    const handleClickAddHospitalImport = useCallback(() => {
      setIsCreateModalOpen(true);
    }, []);

    const handleCloseCreateModal = useCallback(() => {
      setIsCreateModalOpen(false);
    }, []);

    const handleDownloadTemplate = useCallback(async () => {
      const { data } = await claimRequestExcelTemplates();
      console.log(data);

      ExcelGenerator.generate({
        fileName: `Mou-Template-${formatDate(new Date(), "yyyyMMddhhmmss")}`,
        configs: [{ columns: data.claimRequestExcelTemplates }],
      });
    }, [claimRequestExcelTemplates]);

    const pageActions = useMemo(
      () => [
        {
          children: t(".template"),
          onClick: handleDownloadTemplate,
          variant: "outlined",
          permittedRoles: [PERMISSIONS.HOSPITAL_IMPORT_EDIT],
        },
        {
          children: t(".addHospitalImport"),
          startIcon: <AddIcon />,
          onClick: handleClickAddHospitalImport,
          color: "primary",
          permittedRoles: [PERMISSIONS.HOSPITAL_IMPORT_EDIT],
        },
      ],
      [handleClickAddHospitalImport, handleDownloadTemplate, t]
    );

    return {
      breadcrumbs,
      pageActions,
      isCreateModalOpen,
      handleCloseCreateModal,
    };
  })
);

export default enhancer(HospitalImportPage);
